import { resolveUploadUrl, slug, stamp } from "./exportUpgrades";
import { buildZip, saveBlob, type ZipEntry } from "./zip";

/**
 * The fellowship candidates' citations, downloaded as one zip.
 *
 * A citation is read out at investiture, so the whole set has to be collected,
 * printed and put in order — and it was being gathered by opening each
 * application and right-clicking, the same way the photographs were before the
 * bundle next door. This takes the set the list is already showing and hands it
 * over in one go, named so the files can be sorted without opening them:
 * `Name_MembershipNo`.
 *
 * FELLOWSHIP ONLY. The citation is step 9 of the wizard and that step exists
 * for a Member → Fellow upgrade and no other, so `citation_file` is null on
 * every row of every other category. The button is therefore offered on the
 * Fellow page alone — a Citations export on the Member list would be a dialog
 * whose every line read "none on file".
 *
 * ── One slot, two kinds of file ─────────────────────────────────────────────
 * Unlike a photograph, a citation has no fallback: there is one column and
 * either the candidate lodged it or they did not. But it may be a PDF *or* a
 * scan — the upload accepts pdf, jpg, jpeg and png — so the extension is taken
 * from what the server says the file is rather than guessed from the slot, and
 * a mixed archive of PDFs and images is the normal case rather than a fault.
 *
 * The files are fetched from the API host's public storage, which already
 * answers with permissive CORS headers, so this needs nothing from the server.
 */

export type CitationJob = {
  applicationId: string | number;
  member: string;
  membershipNo: string;
  url: string;
  /** Path inside the archive, extension appended once the type is known. */
  path: string;
};

/** A candidate who has lodged no citation at all. */
export type MissingCitation = {
  applicationId: string | number;
  member: string;
  membershipNo: string;
};

/** A citation that is on the record but could not be downloaded. */
export type FailedCitation = MissingCitation & { url: string; reason: string };

export type CitationPlan = {
  jobs: CitationJob[];
  missing: MissingCitation[];
  /** Candidates covered, whether or not they lodged one. */
  candidates: number;
  /** Candidates with a citation on file — what the archive will hold. */
  withCitation: number;
  /** Candidates with nothing on file. */
  withNone: number;
};

export type CitationExportReport = {
  filename: string;
  saved: number;
  failed: FailedCitation[];
  missing: MissingCitation[];
  bytes: number;
};

/* ── Naming ──────────────────────────────────────────────────────────────── */

/**
 * `Name_MembershipNo`, safe to write to disk on any of the three platforms the
 * secretariat uses.
 *
 * Spaces and case are kept — these files are read by people, and "ADEYEMI JOHN"
 * beats "adeyemi-john" in a folder being worked through at an investiture.
 * Only what a filesystem genuinely refuses is stripped: the reserved
 * punctuation, control characters, and the trailing dots/spaces Windows
 * silently drops.
 *
 * Deliberately the same rule as the photograph bundle's, so a candidate's
 * citation and their photograph carry the SAME filename in their two archives
 * and can be matched by sorting both folders.
 */
export function citationFileBase(row: any): string {
  const name = String(row?.user?.name ?? "").trim() || "Unnamed candidate";
  const membershipNo = String(row?.user?.member?.membership_no ?? "").trim();

  const clean = (text: string) =>
    text
      // eslint-disable-next-line no-control-regex
      .replace(/[<>:"/\\|?*\u0000-\u001f]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      // Windows silently drops a trailing dot or space, so a name ending in one
      // would not be the name the file actually ends up carrying.
      .replace(/[. ]+$/, "");

  const base = clean(name);
  const no = clean(membershipNo);

  // No membership number is a real state (a record that predates one). Better a
  // file named after the person alone than one ending in a bare underscore that
  // reads like a truncation.
  return no ? `${base}_${no}` : base;
}

/** Where a row's citation lives, or "" when none was lodged. */
export function citationUrl(row: any): string {
  return resolveUploadUrl(row?.citation_file);
}

/* ── What would be exported ──────────────────────────────────────────────── */

export function planApplicantCitations(rows: any[]): CitationPlan {
  const jobs: CitationJob[] = [];
  const missing: MissingCitation[] = [];
  // One folder is one namespace: two candidates sharing a name AND lacking a
  // membership number would otherwise write over each other, and the archive
  // would come out short with nothing said about it.
  const taken = new Set<string>();

  for (const row of rows) {
    const member = String(row?.user?.name ?? "").trim() || "Unnamed candidate";
    const membershipNo = String(row?.user?.member?.membership_no ?? "").trim();
    const url = citationUrl(row);

    if (!url) {
      missing.push({ applicationId: row.id, member, membershipNo });
      continue;
    }

    const base = citationFileBase(row);
    let name = base;
    for (let n = 2; taken.has(name.toLowerCase()); n++) {
      name = `${base} (${n})`;
    }
    taken.add(name.toLowerCase());

    jobs.push({ applicationId: row.id, member, membershipNo, url, path: name });
  }

  return {
    jobs,
    missing,
    candidates: rows.length,
    withCitation: jobs.length,
    withNone: missing.length,
  };
}

/* ── Downloading ─────────────────────────────────────────────────────────── */

/** What the file is, taken from the server rather than guessed from the URL. */
const EXTENSION_BY_TYPE: Record<string, string> = {
  "application/pdf": "pdf",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/tiff": "tiff",
};

/** The types a citation may legitimately be — the upload rule, mirrored. */
function isCitationType(contentType: string): boolean {
  const type = contentType.split(";")[0].trim().toLowerCase();
  return type === "application/pdf" || type.startsWith("image/");
}

function extensionFor(contentType: string, url: string): string {
  const byType = EXTENSION_BY_TYPE[contentType.split(";")[0].trim().toLowerCase()];
  if (byType) return byType;

  const fromUrl = url.split("?")[0].split("#")[0].match(/\.([a-z0-9]{2,5})$/i);
  return fromUrl ? fromUrl[1].toLowerCase() : "pdf";
}

/** A downloaded file: the bytes, and what the server said it is. */
type Fetched = { data: Uint8Array; extension: string };

async function fetchOnce(url: string): Promise<Fetched> {
  const res = await fetch(url, { credentials: "omit" });

  if (!res.ok) {
    throw new Error(res.status === 404 ? "not found on the server" : `server said ${res.status}`);
  }

  const contentType = res.headers.get("content-type") ?? "";

  // A misconfigured host answers a missing file with its own HTML error page
  // and a 200. Writing that into the zip would produce a "citation" that opens
  // as a broken file at the investiture, with nothing to say why.
  if (contentType && !isCitationType(contentType)) {
    throw new Error("the file is not a PDF or a scan");
  }

  const bytes = new Uint8Array(await res.arrayBuffer());
  if (bytes.length === 0) throw new Error("the file is empty");

  return { data: bytes, extension: extensionFor(contentType, url) };
}

/**
 * Fetch, and on failure try once more.
 *
 * Several requests are in flight at a time against a shared host, and a batch
 * that drops a citation to a momentary hiccup is exactly the failure this
 * export exists to prevent. A genuine 404 fails twice and is reported; a blip
 * does not cost anybody their citation.
 */
async function fetchCitation(url: string): Promise<Fetched> {
  try {
    return await fetchOnce(url);
  } catch (first: any) {
    try {
      return await fetchOnce(url);
    } catch {
      throw first;
    }
  }
}

/**
 * Save ONE citation, named the same way the bundle names it.
 *
 * Fetched into memory rather than linked to directly, because the `download`
 * attribute is ignored on a cross-origin href: a plain link to the storage host
 * would open the file in a tab, or save it under its random storage name
 * (`0G7bpH9vUc….pdf`), which is exactly the thing this is meant to avoid.
 *
 * Throws with a readable reason so the caller can offer the raw file instead.
 */
export async function downloadCitation(url: string, fileBase: string): Promise<void> {
  const file = await fetchCitation(url);
  saveBlob(
    new Blob([file.data as BlobPart], { type: "application/octet-stream" }),
    `${fileBase}.${file.extension}`,
  );
}

/**
 * A short sheet inside the archive saying what it is — and, just as importantly,
 * whose citation is NOT in it. An archive of 44 citations handed over for a
 * batch of 50 candidates has six gaps in it, and the person reading the names
 * out needs to know whose they are without counting.
 */
function manifest(
  saved: { entry: ZipEntry; job: CitationJob }[],
  failed: FailedCitation[],
  missing: MissingCitation[],
  meta: { category: string },
): ZipEntry {
  const cell = (value: unknown) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const line = (cells: unknown[]) => cells.map(cell).join(",");

  const lines = [
    line([`NIQS — Fully endorsed candidates' citations, upgrades to ${meta.category}`]),
    line([`Taken on: ${stamp().printed}`]),
    line([`Citations in this archive: ${saved.length}`]),
    ...(failed.length ? [line([`Could not be downloaded: ${failed.length}`])] : []),
    ...(missing.length ? [line([`None on file: ${missing.length}`])] : []),
    "",
    line(["Member", "Membership No", "File", "Note"]),
    ...saved.map((row) => line([row.job.member, row.job.membershipNo, row.entry.name, ""])),
    ...failed.map((f) =>
      line([f.member, f.membershipNo, "", `not downloaded — ${f.reason}`]),
    ),
    ...missing.map((m) => line([m.member, m.membershipNo, "", "no citation on file"])),
  ];

  // The BOM is what makes Excel read this as UTF-8 rather than the local
  // codepage — without it any accented name arrives as mojibake.
  return {
    name: "manifest.csv",
    data: new TextEncoder().encode("﻿" + lines.join("\r\n")),
  };
}

export function citationArchiveName(category: string): string {
  return `upgrade-citations-${slug(category)}-fully-endorsed-${stamp().file}.zip`;
}

/**
 * Fetch every planned citation, bundle them, and hand the archive to the
 * browser. `onProgress` is called after each attempt so the dialog can show
 * where it has got to; a citation that fails is recorded and the rest of the
 * batch carries on — one bad file must not cost the whole export.
 */
export async function exportApplicantCitations(
  plan: CitationPlan,
  meta: { category: string },
  onProgress?: (done: number, total: number) => void,
  concurrency = 5,
): Promise<CitationExportReport> {
  // Keyed by URL rather than by job: two candidates cannot share a citation,
  // but a duplicated application row would otherwise be fetched twice.
  const wanted = plan.jobs
    .map((j) => j.url)
    .filter((url, i, all) => all.indexOf(url) === i);
  const fetched = new Map<string, Fetched>();
  const errors = new Map<string, string>();

  let next = 0;
  let done = 0;

  const worker = async () => {
    for (let i = next++; i < wanted.length; i = next++) {
      const url = wanted[i];
      try {
        fetched.set(url, await fetchCitation(url));
      } catch (e: any) {
        // A cross-origin fetch that is refused outright reports nothing useful,
        // so say what actually happened rather than "undefined".
        errors.set(url, e?.message ? String(e.message) : "the download was blocked");
      } finally {
        onProgress?.(++done, wanted.length);
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, wanted.length) }, worker));

  const saved: { entry: ZipEntry; job: CitationJob }[] = [];
  const failed: FailedCitation[] = [];

  for (const job of plan.jobs) {
    const file = fetched.get(job.url);
    if (file) {
      saved.push({ entry: { name: `${job.path}.${file.extension}`, data: file.data }, job });
    } else {
      failed.push({
        applicationId: job.applicationId,
        member: job.member,
        membershipNo: job.membershipNo,
        url: job.url,
        reason: errors.get(job.url) ?? "the download was blocked",
      });
    }
  }

  if (saved.length === 0) {
    throw new Error(
      failed.length
        ? "None of the citations could be downloaded."
        : "There are no citations to export.",
    );
  }

  // Sorted so the archive opens in a sensible order — the fetches finish out of
  // order, and an archive listed by whichever response came back first is not
  // something anyone can read names from.
  saved.sort((a, b) => a.entry.name.localeCompare(b.entry.name, "en"));

  const sheet = manifest(saved, failed, plan.missing, meta);

  const blob = buildZip([...saved.map((s) => s.entry), sheet]);
  const filename = citationArchiveName(meta.category);
  saveBlob(blob, filename);

  return {
    filename,
    saved: saved.length,
    failed,
    missing: plan.missing,
    bytes: blob.size,
  };
}
